<script lang="ts">
const radiusInit = 150
</script>
<template>
  <DialogUserTemplate class="dialog" v-bind="props" @click="playAnimation">
    <div ref="imageElement" class="image">
      <div class="graphic" />
    </div>
    <div class="content">
      <p ref="contentElement" v-cleaned="descriptionHtml" />
    </div>
    <Teleport to="body">
      <div ref="effectElement" class="effect" :style="styleEffect">
        <div ref="graphicElement" :style="style" class="graphic" />
      </div>
    </Teleport>
  </DialogUserTemplate>
</template>
<script lang="ts" setup>
import { gsap } from 'gsap';

interface Props {
  descriptionHtml: string;
}

const props = defineProps({
  descriptionHtml: null
});

const imageElement = ref(null);

const contentElement = ref(null);

const effectElement = ref(null);

const graphicElement = ref(null);

const { x, y, width, height } = useElementBounding(imageElement);



const animate = reactive({
  radius: radiusInit,
  rotation: 0,
  scale: 1,
  alpha: 1,
});

const style = computed(() => {
  const angleRad = animate.rotation * (Math.PI / 180) + Math.PI / 4;
  const x =
    -Math.cos(angleRad) * animate.radius +
    Math.cos(Math.PI / 4) * animate.radius;
  const y =
    -Math.sin(angleRad) * animate.radius +
    Math.sin(Math.PI / 4) * animate.radius;

  return {
    transform: `translateX(${x}px) translateY(${y}px) rotate(${animate.rotation}deg) scale(${animate.scale})`,
    opacity: animate.alpha,
  };
});

const playAnimation = () => {
  const tl = gsap.timeline({ delay: 0.3 });
  tl.fromTo(
    animate,
    { radius: radiusInit, rotation: 0, alpha: 1, scale: 0 },
    {
      duration: 0.5,
      scale: 1,
      ease: 'back.out(1.7)',
    },
    0
  );
  tl.fromTo(
    imageElement.value,
    {
      alpha: 0,
    },
    {
      duration: 0.3,
      alpha: 0.4,
    },
    1
  );
  tl.to(
    animate,
    {
      duration: 1,
      radius: 100,
      rotation: 360,
      alpha: 0,
      ease: 'back.in(1.7)',
    },
    1
  );
  tl.to(
    animate,
    {
      duration: 0.5,
      scale: 1.4,
      ease: 'back.in(1.7)',
    },
    1
  );
  tl.to(
    animate,
    {
      duration: 0.5,
      scale: 0,
      ease: 'power3.out',
    },
    1.8
  );
  tl.fromTo(
    contentElement.value,
    { opacity: 0, scale: 0.6 },
    { duration: 0.5, opacity: 1, scale: 1, ease: 'power4.out' },
    0.4
  );
};

const styleEffect = computed(() => {
  const offsetY = window?.scrollY || 0;
  return {
    left: `${x.value}px`,
    top: `${y.value + offsetY}px`,
    width: `${width.value}px`,
    height: `${height.value}px`,
  };
});

onMounted(() => {
  playAnimation();
});
</script>
<style lang="scss" scoped>
.dialog {
  max-width: 40rem;
  padding: var(--gap_xl);
  overflow: hidden;
}

.image {
  position: relative;
  width: 100%;
  height: 7rem;
  margin: 4rem 0 3rem;
  margin-bottom: var(--gap_xl);

  // overflow: visible;
  // opacity: 0;
  background-repeat: no-repeat;
  background-position: center;
  background-size: contain;
  filter: grayscale(100%);
}
.graphic {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-image: url('~/assets/images/exercise/exercise-mail-sent.svg');
  background-repeat: no-repeat;
  background-position: center;
  background-size: contain;
}
.content {
  display: flex;
  flex-direction: column;
  gap: var(--gap_l);
  align-items: center;
  overflow: visible;
  p {
    text-align: center;
    @include font('n', 500, 'secondary');
    strong {
      @include font('n', 700, 'secondary');
    }
  }
}
.effect {
  position: absolute;
  z-index: 200;
  pointer-events: none;
}
</style>
